import { gql, useQuery } from "@apollo/client";
import { Card, CircularProgress, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";
import SoftBox from "components/CheckBox";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatDate } from "utils/formatters";

const PARTIAL_CLOSE_STATE = "Cerrado con entrega parcial";
const COMPLETED_WITH_PARTIAL_DELIVERY_STATE = "Completada con entrega parcial";
const COMPLETED_WITHOUT_DELIVERIES_STATE = "Completada sin entregas";

const getEstadoLines = (status) => {
  switch (status) {
    case PARTIAL_CLOSE_STATE:
      return ["Cerrado", "con", "Entrega parcial"];
    case COMPLETED_WITH_PARTIAL_DELIVERY_STATE:
      return ["Completada", "con", "Entrega parcial"];
    case COMPLETED_WITHOUT_DELIVERIES_STATE:
      return ["Completada", "sin", "entregas"];
    default:
      return [status];
  }
};

export default function ModalSearchProvision({ open, handleClose, setSelection }) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const timeoutID = useRef(null);
  const searchRef = useRef(null);

  const { data, loading } = useQuery(gql`
    query GET_PROVISIONES($filter: JSON, $search: String) {
      provisiones(filter: $filter, orderFromToday: true, search: $search, pagination: { page: 1, limit: 20 }) {
        id
        total
        anulada
        fechaVencimiento
        estado
        total
        pedidoCompra{
          id
          nombre
        }
        proveedor {
          id
          razonSocial
          nombre
          apellido
          documento
        }
        detalles{
          id
          cantidad
          cantidadEntregada
          materiaPrimaRecepcion{
            id
            codigo
            tipo
            categoria
            nombre
            idMateriaPrimaReferencia
          }
          materiaPrima{
            id
            codigo
            tipo
            categoria
            nombre
            disabled
            idMateriaPrimaReferencia
          }
        }
      }
    }
  `, {
    variables: {
      filter: {
        anulada: false,
      },
      search
    },
    skip: !open,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (open) {
      if(searchRef.current) searchRef.current.value = "";
    }else{
      setSearch("");
      setSelected(null);
    }
  }, [open]);

  const handleSelect = (item) => {
    setSelected(prev => prev?.id !== item?.id ? item : null);
  };

  const getEstadoColor = (status) => {
    switch (status) {
      case "Pendiente":
        return "success";
      case "Vencida":
        return "error";
      default:
        return "info";
    }
  };

  const handleSearch = (e) => {
    clearTimeout(timeoutID.current);

    const timeID = setTimeout(() => {
      setSearch(e.target.value);
    }, 1000);

    timeoutID.current = timeID;
  };

  return (<Modal
    open={open}
    onClose={handleClose}
    closeAfterTransition
  >
    <Fade in={open}>
      <Card sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxHeight: "90%",
        width: { xs: "94%", sm: "82%", md: "720px" },
        maxWidth: "720px",
        overflow: "hidden",
        p: 3,
      }}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h6">Seleccionar orden de provisión</SoftTypography>
          <Tooltip title="Cerrar" placement="top">
            <SoftButton
              circular
              iconOnly
              color="error"
              variant="gradient"
              onClick={handleClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </Tooltip>
        </SoftBox>

        <SoftBox my={1}>
          <SoftInput
            ref={searchRef}
            name="search"
            onChange={handleSearch}
            placeholder="Buscar"
            icon={{ component: "search", direction: "left" }}
            fullWidth
          />
        </SoftBox>

        <SoftBox
          display="flex"
          flexDirection="column"
          gap={2}
          overflow="hidden auto"
          maxHeight="30rem"
          p={1}
        >
          {loading && (<SoftBox display="flex" justifyContent="center" alignItems="center" height="30rem">
            <CircularProgress size={32} color="uproPink"/>
          </SoftBox>)}
          {!loading && data?.provisiones.map((item) => (
            <SoftBox key={item.id}>
              <Card
                sx={{
                  outline: selected?.id === item.id ? `${getUproColor("uproPink")} solid 2px` : "transparent solid 2px",
                  cursor: "pointer",
                  borderRadius: "14px",
                  transition: "outline-color 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    boxShadow: "0 6px 18px rgba(20, 34, 66, 0.12)",
                  },
                }}
                onClick={() => handleSelect(item)}
              >
                <Grid container spacing={1.5} p={1.5} fontSize="small" alignItems="center">
                  <Grid item xs={12} sm={3} minWidth={0}>
                    <div><b>#{item.id} {item.proveedor?.razonSocial ?? `${item.proveedor?.nombre} ${item.proveedor?.apellido}`}</b></div>
                    <div>{item.proveedor?.documento}</div>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <SoftBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={0.75}>
                      <SoftBadge
                        variant="contained"
                        size="sm"
                        badgeContent={(
                          <SoftBox component="span" display="flex" flexDirection="column" gap="2px">
                            {getEstadoLines(item.estado).map((line) => <span key={line}>{line}</span>)}
                          </SoftBox>
                        )}
                        color={getEstadoColor(item.estado)}
                      />
                      <SoftTypography variant="caption" color="secondary">
                        {formatDate(item.fechaVencimiento, "dd/MM/yyyy")}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={6} sm={4} minWidth={0}>
                    <SoftTypography variant="button" fontWeight="bold" sx={{ overflowWrap: "anywhere" }}>
                      #{item.pedidoCompra.id} {item.pedidoCompra.nombre}
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} sm={2} display="flex" justifyContent={{ xs: "center", sm: "flex-end" }}>
                    <Tooltip title="Ver PDF" placement="top">
                      <SoftButton
                        circular
                        iconOnly
                        color="uproPink"
                        variant="gradient"
                        onClick={() => window.open(`/op/pdf/${item?.id ?? 0}`)}
                      >
                        <Icon>file_open</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Card>
            </SoftBox>
          ))}
        </SoftBox>

      </Card>
    </Fade>
  </Modal>)
};

ModalSearchProvision.defaultProps = {
  open: false,
  handleClose: () => { },
  setSelection: () => { },
};

ModalSearchProvision.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  setSelection: PropTypes.func,
}
